import { useEffect, useState } from 'react'
import './HealthTab.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'
import { useAuth } from '../../AuthProvider'
import { FINDING_TABS, findingCount, STATUS_LABEL, isClosed, statusOf, tabFindingCounts, isOpenTier, isInformational, tierOf, groupByCategory } from './findings'
import { FindingActions, FindingStatusLine } from './FindingStatus'
import { fetchClosedFindings } from './api'
import LoadError from '../../components/LoadError'
import { loadErrorMessage } from '../../utils/loadState'
import EmployeeCell from '../CustomerPortal/Lifeline/EmployeeCell'

const API_BASE = API_BASE_CONFIG
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('xlr8_token') || ''}` })

function sevClass(type) {
  if (type === 'risk') return 'critical'
  if (type === 'insight') return 'warning'
  return 'info'
}
function sevLabel(type) {
  if (type === 'risk') return 'Critical'
  if (type === 'insight') return 'Warning'
  return 'Info'
}

// Column display order: identity first, then employment context, then check-specific.
const DRILL_COL_ORDER = [
  'employee_number', 'first_name', 'last_name',
  'company_code', 'pay_group', 'status',
  'job_code', 'job_title', 'hire_date', 'last_hire_date', 'term_date',
  'salary_or_hourly', 'flsa_status',
]

function sortDrillColumns(cols) {
  if (!cols || !cols.length) return cols
  const ordered = DRILL_COL_ORDER.filter(c => cols.includes(c))
  const rest = cols.filter(c => !DRILL_COL_ORDER.includes(c))
  return [...ordered, ...rest]
}

function fmtColHeader(c) {
  return c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// ─── Single check row with drill ────────────────────────
function CheckRow({ obs, customerId, onOpenEmployee = null }) {
  // the server sends no SQL to a non-admin; the button is admin-only here as well
  const { isAdmin } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const [drillData, setDrillData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showSQL, setShowSQL] = useState(false)
  const [exporting, setExporting] = useState(false)
  // this finding's status as last saved (FindingActions hands back the updated finding)
  const [cur, setCur] = useState(obs)

  const sev = sevClass(obs.type)
  const fyi = isInformational(obs)   // an informational check's finding: labelled FYI, never counted
  const count = findingCount(obs)   // subjects, else people, else records -- never a bare "affected"
  const status = statusOf(cur)
  const isResolved = isClosed(cur)

  const handleExpand = async () => {
    if (!obs.id) return
    if (expanded) { setExpanded(false); return }
    setExpanded(true)
    if (drillData !== null) return
    setLoading(true)
    try {
      const res = await fetch(
        `${API_BASE}/api/observations/${customerId}/drilldown/${obs.id}`,
        { headers: authHeaders() }
      )
      setDrillData(res.ok ? await res.json() : { error: `HTTP ${res.status}` })
    } catch (e) { setDrillData({ error: e.message }) }
    setLoading(false)
  }

  const handleExport = async (e) => {
    e.stopPropagation()
    if (!drillData?.rows?.length) return
    setExporting(true)
    const cols = sortDrillColumns(drillData.columns || Object.keys(drillData.rows[0]))
    const csv = [cols.join(','), ...drillData.rows.map(r =>
      cols.map(c => { const v = r[c] != null ? String(r[c]) : ''; return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v }).join(',')
    )].join('\n')
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `health_${obs.id?.slice(0,8)}.csv`
    })
    a.click()
    setExporting(false)
  }

  return (
    <div className={`ht-row ${isResolved ? 'ht-row-resolved' : ''} ${expanded ? 'ht-row-expanded' : ''}`}>
      <div className="ht-row-top" onClick={handleExpand}>
        <div className={`ht-sev-bar ht-sev-${sev}`} />
        <div className="ht-row-status-col">
          <span className={`ht-status-tag ht-st-${isResolved ? 'resolved' : sev}`}>
            {isResolved ? STATUS_LABEL[status] : fyi ? 'FYI' : sevLabel(obs.type)}
          </span>
        </div>
        <div className="ht-row-body">
          <div className={`ht-row-title ${isResolved ? 'ht-title-dim' : ''}`}>
            {obs.title}
            {!isResolved && status !== 'open' && (
              <span className={`fs-pill fs-pill-${status} fs-pill-inline`}>{STATUS_LABEL[status]}</span>
            )}
          </div>
          <div className="ht-row-detail">{obs.detail}</div>
          {(obs.source || obs.table_name) && (
            <div className="ht-row-source">
              {(obs.source || obs.table_name || '').replace(/^cust_[^_]+(?:_[^_]+)*_api_/, 'api_')}
            </div>
          )}
        </div>
        <div className="ht-row-right">
          {count && (
            <div className="ht-count-wrap">
              <div className={`ht-count ht-count-${sev}`}>{count.value.toLocaleString()}</div>
              <div className="ht-count-label">{count.label}</div>
            </div>
          )}
          {obs.id && !isResolved && (
            <button
              className={`ht-action-btn ht-ab-${sev}`}
              onClick={e => { e.stopPropagation(); handleExpand() }}
            >
              View →
            </button>
          )}
          {obs.id && <span className={`ht-expand-icon ${expanded ? 'open' : ''}`}>▾</span>}
        </div>
      </div>

      {expanded && (
        <div className="ht-drill" onClick={e => e.stopPropagation()}>
          {/* Resolution workflow. Only a scan closes a finding ("Verified & Closed"):
              people acknowledge it and say a fix was submitted; the next scan on newer
              data checks the fix. Which buttons show comes from the viewer's permissions
              (FindingActions); the server enforces the same rules. */}
          {obs.id && (
            <div className="ht-resolution">
              <div className="ht-res-label">Resolution Workflow</div>
              {status !== 'dismissed' && (
              <div className="ht-res-steps">
                <div className="ht-res-step ht-rs-done"><span className="ht-rs-num">✓</span>Detected</div>
                <div className={`ht-res-step ${status === 'open' || status === 'acknowledged' ? 'ht-rs-current' : 'ht-rs-done'}`}>
                  <span className="ht-rs-num">2</span>Under Review
                </div>
                <div className={`ht-res-step ${status === 'fix_submitted' ? 'ht-rs-current' : status === 'resolved' ? 'ht-rs-done' : ''}`}>
                  <span className="ht-rs-num">3</span>Fix Submitted
                </div>
                <div className={`ht-res-step ${status === 'resolved' ? 'ht-rs-done' : ''}`}>
                  <span className="ht-rs-num">4</span>Verified &amp; Closed
                </div>
              </div>
              )}
              <FindingStatusLine finding={cur} />
              <FindingActions finding={cur} onChange={setCur} prefix="ht" />
            </div>
          )}

          {/* Drill data */}
          {loading && <div className="ht-drill-loading">Loading data…</div>}
          {drillData?.error && <div className="ht-drill-error">Failed to load: {drillData.error}</div>}
          {drillData?.rows?.length > 0 && (
            <>
              <div className="ht-drill-header">
                <span className="ht-drill-title">
                  {drillData.total_rows?.toLocaleString() || drillData.rows.length} records
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isAdmin && drillData.sql && (
                    <button className="ht-sql-btn" onClick={() => setShowSQL(s => !s)}>
                      {showSQL ? 'Hide SQL' : 'SQL ▾'}
                    </button>
                  )}
                  <button className="ht-export-btn" onClick={handleExport} disabled={exporting}>
                    ↓ Export CSV
                  </button>
                </div>
              </div>
              {isAdmin && showSQL && drillData.sql && (
                <div className="ht-sql-block"><pre>{drillData.sql}</pre></div>
              )}
              <div className="ht-table-wrap">
                <table className="ht-table">
                  <thead>
                    <tr>{sortDrillColumns(drillData.columns).map(c => <th key={c}>{fmtColHeader(c)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {drillData.rows.slice(0, 20).map((row, i) => (
                      <tr key={i}>
                        {sortDrillColumns(drillData.columns).map(c => <td key={c}>{c === 'employee_number' ? <EmployeeCell number={row[c]} employeeRef={drillData.employee_refs?.[`${row.company_code ?? ''}|${row[c]}`]} onOpenEmployee={onOpenEmployee} /> : (row[c] != null ? String(row[c]) : '—')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {drillData.total_rows > 20 && (
                <div className="ht-drill-more">Showing 20 of {drillData.total_rows.toLocaleString()} rows</div>
              )}
            </>
          )}
          {drillData && !drillData.error && (!drillData.rows?.length) && (
            <div className="ht-drill-note">{drillData.note || 'No row-level data available.'}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Category section ────────────────────────────────────
// One group per check-library category; the heading is the library's category as written.
function CatSection({ category, findings, customerId, onOpenEmployee = null }) {
  const [collapsed, setCollapsed] = useState(false)
  const critCount = findings.filter(f => f.type === 'risk').length
  const warnCount = findings.filter(f => f.type === 'insight').length
  const infoCount = findings.filter(f => f.type === 'note').length

  return (
    <div className="ht-cat">
      <div className={`ht-cat-header ${!collapsed ? 'ht-cat-open' : ''}`} onClick={() => setCollapsed(c => !c)}>
        <div className="ht-cat-left">
          <span className="ht-cat-title">{category}</span>
          <div className="ht-cat-counts">
            {critCount > 0 && <span className="ht-cc ht-cc-critical">{critCount} critical</span>}
            {warnCount > 0 && <span className="ht-cc ht-cc-warning">{warnCount} warning</span>}
            {infoCount > 0 && <span className="ht-cc ht-cc-info">{infoCount} info</span>}
          </div>
        </div>
        <div className="ht-cat-right">
          <span className="ht-cat-count-total">{findings.length} check{findings.length !== 1 ? 's' : ''}</span>
          <span className={`ht-cat-toggle ${collapsed ? '' : 'open'}`}>▾</span>
        </div>
      </div>
      {!collapsed && (
        <div className="ht-cat-body">
          {findings.map((obs, i) => (
            <CheckRow key={obs.id || i} obs={obs} customerId={customerId} onOpenEmployee={onOpenEmployee} />
          ))}
        </div>
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════
export default function HealthTab({ customerId, observations, onOpenEmployee = null }) {
  const [filter, setFilter] = useState('open')
  // The profile carries the ACTIVE findings (open, acknowledged, fix submitted); resolved
  // and dismissed ones come from the findings list, read once per customer.
  const [closed, setClosed] = useState([])
  // A failed read is shown as a failure with a Retry, never as "0 resolved" (item 201).
  const [closedError, setClosedError] = useState(null)
  const [closedTry, setClosedTry] = useState(0)
  useEffect(() => {
    let live = true
    setClosedError(null)
    if (customerId) fetchClosedFindings(customerId)
      .then(rows => { if (live) setClosed(rows) })
      .catch(err => { if (live) { setClosed([]); setClosedError(loadErrorMessage(err)) } })
    return () => { live = false }
  }, [customerId, closedTry])
  const closedLoadError = closedError && (
    <LoadError inline what="resolved and dismissed findings" error={closedError} onRetry={() => setClosedTry(n => n + 1)} />
  )

  // Health = every finding that is not a compliance finding (no compliance rule code).
  const healthObs = (observations || []).filter(FINDING_TABS.health)
  const activeIds = new Set(healthObs.map(o => o.id))
  const closedHealth = closed.filter(o => FINDING_TABS.health(o) && !activeIds.has(o.id))
  // this tab's numbers -- the same function the tab-bar badge reads (findings.tabFindingCounts)
  const { critical: critCount, warning: warnCount, info: infoCount, fyi: fyiCount, open: openCount, active: activeObs } =
    tabFindingCounts(observations, 'health')
  const resolvedCount = closedHealth.filter(o => statusOf(o) === 'resolved').length
  const dismissedCount = closedHealth.filter(o => statusOf(o) === 'dismissed').length

  const filtered = (filter === 'resolved' || filter === 'dismissed'
    ? closedHealth.filter(o => statusOf(o) === filter)
    : activeObs.filter(o => {
        if (filter === 'open') return isOpenTier(o)
        if (['critical', 'warning', 'info', 'fyi'].includes(filter)) return tierOf(o) === filter
        return true
      }))

  // Grouped by each finding's check-library category (findings.groupByCategory):
  // alphabetical, Uncategorized last.
  const groups = groupByCategory(filtered)

  if (!observations?.length && !closed.length) {
    if (closedError) return <div className="ht-root">{closedLoadError}</div>
    return (
      <div className="ht-empty">
        <div className="ht-empty-icon">⚙</div>
        <div className="ht-empty-title">No health checks run yet</div>
        <div className="ht-empty-sub">Generate observations to see configuration quality checks for this customer.</div>
      </div>
    )
  }

  return (
    <div className="ht-root">
      {/* Summary */}
      <div className="ht-summary">
        <div className="ht-sum-stat">
          <div className={`ht-sum-val ${critCount > 0 ? 'critical' : 'clear'}`}>{critCount}</div>
          <div className="ht-sum-label">Critical</div>
          <div className="ht-sum-sub">Fix before next payroll</div>
        </div>
        <div className="ht-sum-stat">
          <div className={`ht-sum-val ${warnCount > 0 ? 'warning' : 'clear'}`}>{warnCount}</div>
          <div className="ht-sum-label">Warning</div>
          <div className="ht-sum-sub">Degraded data quality</div>
        </div>
        <div className="ht-sum-stat">
          <div className="ht-sum-val info">{infoCount}</div>
          <div className="ht-sum-label">Info</div>
          <div className="ht-sum-sub">Monitor / review</div>
        </div>
        <div className="ht-sum-stat">
          <div className="ht-sum-val resolved">{closedError ? '—' : resolvedCount}</div>
          <div className="ht-sum-label">Resolved</div>
          <div className="ht-sum-sub">Closed by a scan</div>
        </div>
        <div className="ht-sum-stat">
          <div className="ht-sum-val total">{healthObs.filter(o => !isInformational(o)).length}</div>
          <div className="ht-sum-label">Findings</div>
          <div className="ht-sum-sub">Checks with results</div>
        </div>
      </div>

      {closedLoadError}

      {/* Filter bar */}
      <div className="ht-filter-bar">
        {[
          { key: 'open',     label: `Open ${openCount}` },
          { key: 'critical', label: `🔴 Critical ${critCount}` },
          { key: 'warning',  label: `🟡 Warning ${warnCount}` },
          { key: 'info',     label: `🔵 Info ${infoCount}` },
          // FYI findings (informational checks) are listed here only, counted nowhere
          ...(fyiCount > 0 ? [{ key: 'fyi', label: `FYI ${fyiCount}` }] : []),
          { key: 'resolved', label: `✓ Resolved ${resolvedCount}` },
          { key: 'dismissed', label: `Dismissed ${dismissedCount}` },
        ].map(f => (
          <button
            key={f.key}
            className={`ht-filter-chip ${filter === f.key ? `active ht-fc-${f.key}` : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {groups.length === 0 && (
        <div className="ht-no-results">
          No {filter} checks for this customer.
        </div>
      )}

      {groups.map(([category, findings]) => (
        <CatSection key={category} category={category} findings={findings} customerId={customerId} onOpenEmployee={onOpenEmployee} />
      ))}
    </div>
  )
}

